import type { Editor } from "@progress/kendo-react-editor";
/**
 * Internal properties that may exist on Kendo Editor instances
 * Uses intersection type to avoid interface extension conflicts
 *
 * @remarks
 * Kendo Editor wraps ProseMirror internally but doesn't expose its types.
 * This type definition captures the internal structure we need to interact with
 * ProseMirror's state, schema, and transaction API.
 */
export type KendoEditorInternal = {
  element?: HTMLElement;
  view?: {
    dom?: HTMLElement;
    focus?: () => void;
    hasFocus?: () => boolean;
    state?: {
      doc?: unknown;
      selection?: unknown;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      schema?: any;
      tr?: {
        replaceSelection?: (slice: unknown) => unknown;
        replaceSelectionWith?: (
          node: unknown,
          inheritMarks: boolean
        ) => unknown;
        insert?: (pos: number, node: unknown) => unknown;
        delete?: (from: number, to: number) => unknown;
      };
    };
    dispatch?: (transaction: unknown) => void;
  };
  value?: string;
  setValue?: (content: string) => void;
};

/**
 * Type guard to check if an editor instance has internal properties
 */
const hasEditorInternals = (
  editor: Editor
): editor is Editor & KendoEditorInternal => {
  return editor && typeof editor === "object";
};
/**
 * Type guard to check if an element is an HTMLElement
 * Provides better type safety than direct casting
 */
export const isHTMLElement = (element: unknown): element is HTMLElement => {
  return element instanceof HTMLElement;
};
/**
 * Safely gets the editor element from a Kendo Editor ref
 * Returns null if the editor or its element is not available
 */
export const getEditorElement = (
  editorRef: React.RefObject<Editor>
): HTMLElement | null => {
  const editorInstance = editorRef.current;
  if (!editorInstance || !hasEditorInternals(editorInstance)) {
    return null;
  }

  const element = editorInstance.element;
  return isHTMLElement(element) ? element : null;
};
/**
 * Safely gets the ProseMirror view from a Kendo Editor ref
 * Returns null if the view is not available
 */
export const getEditorView = (
  editorRef: React.RefObject<Editor>
): KendoEditorInternal["view"] | null => {
  const editorInstance = editorRef.current;
  if (!editorInstance || !hasEditorInternals(editorInstance)) {
    return null;
  }

  const view = editorInstance.view;
  return view && view.dom ? view : null;
};

/**
 * Checks if the editor currently has focus
 * More reliable than checking document.activeElement directly
 */
export const isEditorFocused = (
  editorRef: React.RefObject<Editor>
): boolean => {
  const editorElement = getEditorElement(editorRef);
  if (!editorElement) {
    return false;
  }

  const activeElement = document.activeElement;
  if (!activeElement) {
    return false;
  }

  // Check if active element is within the editor
  if (editorElement.contains(activeElement)) {
    return true;
  }

  // Check ProseMirror view focus state
  const view = getEditorView(editorRef);
  if (view && view.hasFocus && typeof view.hasFocus === "function") {
    return view.hasFocus();
  }

  return false;
};
/**
 * Checks if an element is part of a color picker
 * Useful for handling color picker events
 */
export const isColorPickerElement = (element: HTMLElement): boolean => {
  return !!(
    element.classList.contains("k-colorpalette-tile") ||
    element.closest(".k-colorpalette-tile") ||
    element.closest(".k-colorpicker-popup") ||
    element.closest(".k-popup")
  );
};
